/**
 * VISNINGSVALGET, HUSKET PER MENNESKE (QA13 runde 2, #39 / K-18 / J9).
 *
 * =================================================================================================
 * Atle, K-18: «Tvitvi husker brukerens visningsvalg. Hvilke arrangementer man vil se, filtre og
 * gruppering lagres per bruker på serveren, slik at det ikke må settes hver gang, og speiles i
 * URL-en for deling.»
 *
 * =================================================================================================
 * TO KILDER TIL DEN SAMME TILSTANDEN, OG HVEM SOM VINNER.
 *
 * URL-en er for DELING — «se dette» — og den vinner alltid når den sier noe. En lenke en kollega
 * har sendt skal vise det hen så, ikke det du så sist. Raden her er for GJENOPPTAKELSE: den svarer
 * når adressen ikke sier noe, altså når man åpner siden fra menyen.
 *
 * Det er den samme rangeringen `?view=` allerede har mot standardvisningen, og den er verdt å
 * skrive ned fordi den motsatte er fristende: en lagret preferanse som overstyrte lenken ville
 * gjort deling umulig å stole på.
 *
 * =================================================================================================
 * VERDIEN ER ÉN JSONB OG IKKE FEM KOLONNER.
 *
 * Valget er sammensatt — hvilke arrangementer, hvilke filtre, hvilken gruppering — og det skrives
 * alltid i sin helhet. Fem kolonner ville betydd fem migreringer neste gang en visning får et
 * filter til.
 *
 * KLIENTEN EIER FORMEN, og serveren lagrer den. Et `z.record` her framfor et navngitt skjema er
 * derfor ikke slapphet: det er den ene tabellen i produktet der innholdet er en klientpreferanse og
 * ikke en forretningsopplysning, og et skjema over den ville måttet utvides hver gang en side fikk
 * et nytt filter. Grensen er SKRANKEN — et objekt, ikke en liste — og den står i basen.
 */
import { z } from 'zod';

/**
 * Hvor mange nøkler ett visningsvalg kan ha.
 *
 * Nok til enhver skjerm i produktet, og lavt nok til at en ødelagt klient ikke kan skrive et
 * kilobyte-objekt per tastetrykk.
 */
export const MAX_VIEW_PREF_KEYS = 40;

const viewPrefValueSchema = z
  .record(z.string(), z.unknown())
  .refine((value) => Object.keys(value).length <= MAX_VIEW_PREF_KEYS, {
    message: `A view preference holds at most ${String(MAX_VIEW_PREF_KEYS)} keys.`,
  });

export const viewPrefsDtoSchema = z
  .object({
    viewKey: z.string(),
    /**
     * Et tomt objekt betyr «ingen lagret preferanse» — altså sidens egen standard. Det er den
     * samme tilstanden som en manglende rad, så klienten trenger bare å kjenne den ene.
     */
    value: z.record(z.string(), z.unknown()),
  })
  .meta({ id: 'ViewPrefsDto' });
export type ViewPrefsDto = z.infer<typeof viewPrefsDtoSchema>;

export const putViewPrefsRequestSchema = z
  .object({ value: viewPrefValueSchema })
  .meta({ id: 'PutViewPrefsRequest' });
export type PutViewPrefsRequest = z.infer<typeof putViewPrefsRequestSchema>;

/**
 * VISNINGSNØKKELEN ER EN RUTEPARAMETER, OG DERFOR HAR DEN ET SKJEMA.
 *
 * Formen er `<visning>:<org-id>` — «calendar:018f…». Kolonet er med vilje: huset ligger i nøkkelen
 * og ikke i en kolonne (se migrering 0318), og uten et skilletegn ville to hus med hver sin
 * kalender delt rad.
 *
 * `ck_user_view_prefs__view_key` slipper 1–120 tegn igjennom, og alt over det blir en databasefeil
 * framfor en 400. Derfor står lengden her også.
 */
export const viewKeySchema = z
  .string()
  .trim()
  .min(1)
  .max(120)
  .regex(
    /^[a-z][a-z0-9-]*:[0-9a-f-]{36}$/,
    'A view key is a lower-case name, a colon and an organisation id.',
  );

export const viewPrefsParamsSchema = z
  .object({ viewKey: viewKeySchema })
  .meta({ id: 'ViewPrefsParams' });
export type ViewPrefsParams = z.infer<typeof viewPrefsParamsSchema>;
